<header class="hero-section" role="banner">
  <div class="hero-content wrap">
    <div class="badge">Built with Angularize</div>
    <h1 class="hero-title">
      Engineering the future of <span class="text-accent">Angular</span>
    </h1>
    <p class="hero-lead">
      We’re a team of developers and designers dedicated to removing the
      friction between an idea and a production-ready application.
    </p>
    <div class="hero-stats">
      <div class="stat-item">
        <strong>100+</strong> <span>Early Users</span>
      </div>
      <div class="stat-divider"></div>
      <div class="stat-item">
        <strong>1-Click</strong> <span>Deployment</span>
      </div>
    </div>
  </div>
  <div class="hero-bg-glow"></div>
</header>

<main id="content" role="main">
  <section class="wrap section">
    <div class="mission-layout">
      <div class="mission-box">
        <span class="sub-label">The Mission</span>
        <h3 class="section-title-sm">Build Apps like Google</h3>
        <p class="mission-text">
          Angularize lets developers describe their vision and receive a
          production-ready application in minutes. We leverage
          <strong>Angular Material best practices</strong> to remove boilerplate
          and enforce consistency, ensuring your architecture is as clean as an
          enterprise-grade Google product.
        </p>
      </div>

      <div class="vision-box">
        <span class="sub-label">The Vision</span>
        <h3 class="section-title-sm">No More Learning Curves</h3>
        <p class="mission-text">
          We are <strong>killing the high learning curve</strong> of Angular
          when compared to NextJS framework. By automating complex architecture
          and boilerplate, we preserve full developer control while making
          enterprise-grade development instant, consistent, and accessible to
          every "Angular lover."
        </p>
      </div>
    </div>
  </section>

  <section class="wrap section highlight-box">
    <div class="founder-layout">
      <div class="founder-media">
        <img
          src="/mahbob.jfif"
          alt="Mahbob Habeb Al-Homidy"
          class="founder-avatar"
        />
      </div>
      <div class="founder-text">
        <span class="sub-label">The Infinite Builder</span>
        <h2 class="founder-name">Mahbob Habeb Al-Homidy</h2>
        <p class="founder-bio">
          Mahbob is an
          <strong>AI Engineer and Cloud Specialist</strong>, bridge-building
          between academic excellence and cutting-edge industry application.
          Currently a
          <strong>Generative AI Engineer at Pythrust Technologies</strong> , he
          specializes in developing intelligent solutions using
          <strong>Next.js, FastAPI, and Python</strong>. <br /><br />
          With a "builder-first" approach to the modern stack, he has earned
          prestigious certifications including
          <strong>AWS Certified Cloud Practitioner</strong> and completed
          <strong>Google's AI Agents Intensive</strong>. Beyond engineering, he
          serves as an
          <strong>AI Associate Intern at Rouge International</strong> , where he
          leverages Generative AI tools to solve complex technical challenges.
          An IT scholar at MGM's College of CS and IT, Mahbob is architecting
          the future of automated intelligence—one agent at a time.
        </p>
        <div class="social-links">
          <a
            target="_blank"
            href="mailto:mahbobhabebalhomidy25@gmail.com"
            class="social-tag-P"
          >
            Contact Builder ↗
          </a>

          <a
            href="https://www.linkedin.com/in/mahbob-habeb-al-homidy-984620355"
            target="_blank"
            class="social-tag linkedin-link"
          >
            LinkedIn ↗
          </a>
        </div>
      </div>
    </div>
  </section>

  <section class="wrap section highlight-box">
    <div class="founder-layout">
      <div class="founder-media">
        <img
          src="/maddy.jpeg"
          alt="Madhan Mohan Naidu Mareneni"
          class="founder-avatar"
        />
      </div>
      <div class="founder-text">
        <span class="sub-label">The Unstoppable Architect</span>
        <h2 class="founder-name">Madhan Mohan Naidu Mareneni</h2>
        <p class="founder-bio">
          Madhan is an
          <strong>AI Engineer and Cybersecurity Specialist</strong>, bridging
          the gap between autonomous intelligence and digital defense. Currently
          a <strong>Generative AI Engineer at Pythrust Technologies</strong>, he
          is a key contributor to <strong>Angularize.dev</strong>, where he
          builds high-value applications that move beyond simple chatbots into
          the realm of robust, autonomous agents. <br /><br />
          With a deep foundation in
          <strong>Cyber Security and Digital Forensics</strong> from VIT Bhopal
          University, Madhan has published research on vulnerability scanning
          and architected full SOC pipelines using Wazuh and Cortex. Driven by a
          "builder-first" mindset, he has engineered sophisticated tools like
          the <strong>Multi-Doc Pro RAG Pipeline</strong> and voice-enabled
          assistants like <strong>AI Palate</strong>. Mastering the future-tech
          stack of <strong>LangGraph, LangChain, and Google ADK</strong>, he is
          dedicated to constructing intelligent systems that solve complex,
          real-world business challenges.
        </p>
        <div class="social-links">
          <a
            target="_blank"
            href="mailto:madhanmohan1413@gmail.com"
            class="social-tag-P"
          >
            Contact Builder ↗
          </a>

          <a
            href="https://www.linkedin.com/in/madhan-mohan-naidu-mareneni-861443340"
            target="_blank"
            class="social-tag linkedin-link"
          >
            LinkedIn ↗
          </a>
        </div>
      </div>
    </div>
  </section>

  <section class="wrap section highlight-box">
    <div class="founder-layout">
      <div class="founder-media">
        <img src="/founder.jpeg" alt="Ankit Singh" class="founder-avatar" />
      </div>
      <div class="founder-text">
        <span class="sub-label">The Venture Engineer</span>
        <h2 class="founder-name">Ankit Singh</h2>
        <p class="founder-bio">
          Ankit is a
          <strong>Product Manager turned full-stack developer</strong>, blending
          high-level strategy with hands-on technical execution. At Unicommerce
          (SoftBank-funded), he led product integrations handling
          <strong>$3B+ in annual GMV</strong> , later scaling the Safalta
          platform to <strong>1M+ users</strong>
          within two months.
          <br /><br />
          Driven by a "builder-first" mindset, he has personally
          <strong>architected 15+ Angular products</strong> and delivered
          enterprise solutions for global leaders like
          <strong>Mercedes-Benz</strong> and the <strong>Bihani Group</strong>.
          An IIM Kozhikode and DCE alumnus, Ankit leverages his experience as a
          multi-exit founder to build Angularize.dev a platform designed by a
          PM, coded by an engineer, and optimized for scale.
        </p>
        <div class="social-links">
          <a href="https://pythrust.com" target="_blank" class="social-tag-P">
            Founder, Pythrust ↗
          </a>

          <a
            href="https://www.iimk.ac.in/"
            target="_blank"
            class="social-tag-M"
          >
            IIM Alum ↗
          </a>

          <a
            href="https://www.linkedin.com/in/singhankit91/"
            target="_blank"
            class="social-tag linkedin-link"
          >
            LinkedIn ↗
          </a>
        </div>
      </div>
    </div>
  </section>

  <section class="wrap section">
    <div class="section-header">
      <h2 class="section-title">Other Contributors</h2>
      <p>The talented minds building the next generation of web tools.</p>
    </div>

    <div class="grid-layout">
      <div class="contributor-card" *ngFor="let person of contributors">
        <div class="card-header">
          <div class="mini-avatar" [class.accent-bg]="person?.isAccent">
            {{ person?.initials }}
          </div>
          <!-- <div class="status-dot" [class.offline]="!person?.isOnline"></div> -->
        </div>
        <h3>{{ person?.name }}</h3>
        <p class="role">{{ person?.role }}</p>
      </div>
      <div class="contributor-card join-card" routerLink="/contact-us">
        <div class="card-header">
          <div class="mini-avatar plus-btn">
            <span class="fas fa-plus">+</span>
          </div>
        </div>
        <h3>Join our team</h3>
        <p class="role">Ship the Future with Us</p>
      </div>
    </div>
  </section>

  <!-- <section class="wrap section past-contributors">
    <div class="section-header">
      <h2 class="section-title-sm">Past Contributors</h2>
    </div>
    <div class="grid-layout small">
      <div class="past-card">
        <h4>Former Team Member</h4>
        <span>2024 Design Lead</span>
      </div>
      <div class="past-card">
        <h4>Early Dev Contributor</h4>
        <span>v1.0 Core Team</span>
      </div>
    </div>
  </section> -->

  <section class="wrap section cta-section">
    <div class="cta-box">
      <div class="cta-content">
        <span class="sub-label">Join the Movement</span>
        <h2 class="section-title">Want to be part of our journey?</h2>
        <p class="cta-text">
          Whether you're a developer looking to contribute or an organization
          looking to build at scale, we'd love to hear from you.
        </p>
        <div class="cta-actions">
          <a target="_blank" routerLink="/contact-us" class="btn-primary">
            Contact Us
          </a>

          <div class="email-display">
            <span class="email-label">Drop us a line at:</span>
            <a href="mailto:angularize.dev@gmail.com" class="email-link">
              angularize.dev&#64;gmail.com
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
</main>
